// src/context/AuthContext.js (修复日期格式)
import React, { createContext, useState, useContext } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // 从 localStorage 初始化用户信息
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // 从 localStorage 初始化登录状态
    return localStorage.getItem('isAuthenticated') === 'true';
  });
  const [users, setUsers] = useState(() => {
    // 从 localStorage 加载已注册的用户
    const savedUsers = localStorage.getItem('registeredUsers');
    return savedUsers ? JSON.parse(savedUsers) : [];
  });
  const [posts, setPosts] = useState(() => {
    // 从 localStorage 加载文章
    const savedPosts = localStorage.getItem('blogPosts');
    return savedPosts ? JSON.parse(savedPosts) : [];
  });

  // 保存用户到 localStorage
  const saveUsers = (usersList) => {
    localStorage.setItem('registeredUsers', JSON.stringify(usersList));
  };

  // 保存文章到 localStorage
  const savePosts = (postsList) => {
    localStorage.setItem('blogPosts', JSON.stringify(postsList));
  };

  const login = (username, password) => {
    // 检查用户是否存在且密码匹配
    const foundUser = users.find(user => 
      user.username === username && user.password === password
    );

    if (foundUser) {
      // 登录成功
      setUser(foundUser);
      setIsAuthenticated(true);
      localStorage.setItem('user', JSON.stringify(foundUser));
      localStorage.setItem('isAuthenticated', 'true');
      return { success: true, message: '登录成功！' };
    } else {
      return { success: false, message: '用户名或密码错误！' };
    }
  };

  const register = (userData) => {
    // 检查用户名是否已存在
    const userExists = users.some(user => user.username === userData.username);
    if (userExists) {
      return { success: false, message: '用户名已存在！' };
    }

    // 添加新用户
    const newUser = {
      id: users.length > 0 ? Math.max(...users.map(u => u.id)) + 1 : 1,
      ...userData,
      dateRegistered: new Date().toISOString() // 使用 ISO 字符串格式
    };
    
    const updatedUsers = [...users, newUser];
    setUsers(updatedUsers);
    saveUsers(updatedUsers);
    
    return { success: true, message: '注册成功！请登录。' };
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
  };

  // 更新用户信息
  const updateUserInfo = (updatedInfo) => {
    if (!user) return { success: false, message: '用户未登录' };
    
    const updatedUser = { ...user, ...updatedInfo };
    setUser(updatedUser);
    
    // 更新全局用户列表
    const updatedUsersList = users.map(u => 
      u.id === updatedUser.id ? updatedUser : u
    );
    setUsers(updatedUsersList);
    saveUsers(updatedUsersList);
    
    // 更新 localStorage 中的用户信息
    localStorage.setItem('user', JSON.stringify(updatedUser));
    
    return { success: true, message: '用户信息更新成功！' };
  };

  // 发布新文章
  const publishPost = (postData) => {
    const newPost = {
      id: posts.length > 0 ? Math.max(...posts.map(p => p.id)) + 1 : 1,
      ...postData,
      date: new Date().toLocaleDateString('zh-CN'), // 使用本地化日期字符串
      author: user.name || user.username,
      authorId: user.id
    };
    
    const updatedPosts = [newPost, ...posts]; // 新文章放在前面
    setPosts(updatedPosts);
    savePosts(updatedPosts);
    
    return { success: true, message: '文章发布成功！', post: newPost };
  };

  // 删除文章
  const deletePost = (postId) => {
    const updatedPosts = posts.filter(post => post.id !== postId);
    setPosts(updatedPosts);
    savePosts(updatedPosts);
    return { success: true, message: '文章删除成功！' };
  };

  // 获取所有文章
  const getAllPosts = () => {
    return posts;
  };

  // 获取用户的文章
  const getUserPosts = () => {
    if (!user) return [];
    return posts.filter(post => post.authorId === user.id);
  };

  // 社交登录方法
  const socialLogin = async (socialType, socialUserData) => {
    try {
      // 检查用户是否已存在（通过社交ID）
      let foundUser = users.find(user => 
        user.socialId && user.socialId[socialType.toLowerCase()] === socialUserData.id
      );

      if (!foundUser) {
        // 如果用户不存在，创建新用户
        const newUser = {
          id: users.length > 0 ? Math.max(...users.map(u => u.id)) + 1 : 1,
          username: socialUserData.username || socialUserData.name || `social_user_${socialUserData.id}`,
          email: socialUserData.email || '',
          name: socialUserData.name || socialUserData.username || `用户${socialUserData.id}`,
          avatar: socialUserData.avatar || socialUserData.picture || socialUserData.avatar_url || '',
          skills: 'React, JavaScript',
          interests: '编程, 学习',
          github: socialUserData.html_url || '',
          homepage: socialUserData.blog || '',
          description: socialUserData.bio || '欢迎来到我的博客！',
          socialId: {
            [socialType.toLowerCase()]: socialUserData.id
          },
          dateRegistered: new Date().toISOString() // 使用 ISO 字符串格式
        };

        const updatedUsers = [...users, newUser];
        setUsers(updatedUsers);
        saveUsers(updatedUsers);
        foundUser = newUser;
      }

      // 登录用户
      setUser(foundUser);
      setIsAuthenticated(true);
      localStorage.setItem('user', JSON.stringify(foundUser));
      localStorage.setItem('isAuthenticated', 'true');

      return { success: true, message: `${socialType}登录成功！`, user: foundUser };
    } catch (error) {
      console.error(`社交登录失败:`, error);
      return { success: false, message: `${socialType}登录失败: ${error.message}` };
    }
  };

  // 初始化登录状态
  React.useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedAuthStatus = localStorage.getItem('isAuthenticated');
    const savedPosts = localStorage.getItem('blogPosts');
    
    if (storedUser && storedAuthStatus === 'true') {
      setUser(JSON.parse(storedUser));
      setIsAuthenticated(true);
    }
    
    if (savedPosts) {
      setPosts(JSON.parse(savedPosts));
    }
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      login, 
      register, 
      logout, 
      updateUserInfo,
      users,
      publishPost,
      deletePost,
      getAllPosts,
      getUserPosts,
      posts,
      socialLogin // 添加社交登录功能
    }}>
      {children}
    </AuthContext.Provider>
  );
};